import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card, Badge, DataTable, Modal } from "../components/ui";
import FornitoreConfigDocTab from "../components/FornitoreConfigDocTab";
import FornitoreApiTab from "../components/FornitoreApiTab";

const STATO_OPTIONS = {
  100000000: { label: "Attivo", variant: "success" },
  100000001: { label: "Sospeso", variant: "warning" },
  100000002: { label: "Cessato", variant: "danger" },
};

const MACROCATEGORIE = {
  100000000: "Manutenzione",
  100000001: "Servizi",
  100000002: "Forniture",
  100000003: "Consulenza",
};

// Tipi documento "fornitore-level" (collegati direttamente al fornitore)
const TIPI_DOC_FORNITORE = [
  { value: 100000005, label: "Certificazione" },
  { value: 100000007, label: "DURC" },
  { value: 100000006, label: "Polizza assicurativa" },
  { value: 100000008, label: "Visura camerale" },
  { value: 100000011, label: "Altro" },
];

const TIPI_DOC_FORNITORE_MAP = TIPI_DOC_FORNITORE.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

// Suggerimenti tag piu comuni per categoria (autocomplete)
const TAG_SUGGERITI = {
  100000005: [
    "ISO 9001:2015",
    "ISO 14001:2015",
    "ISO 45001:2018",
    "SOA",
    "Antimafia",
    "Iscrizione Albo",
  ],
  100000007: ["DURC"],
  100000006: ["RCT/RCO", "All Risks", "Polizza Professionale"],
  100000008: ["Visura camerale"],
  100000011: [],
};

// Stato scadenza basato sui giorni residui
function statoScadenza(dataScadenza) {
  if (!dataScadenza) return { label: "Senza scadenza", variant: "default" };
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  const scad = new Date(dataScadenza);
  scad.setHours(0, 0, 0, 0);
  const giorni = Math.ceil((scad - oggi) / (1000 * 60 * 60 * 24));
  if (giorni < 0)
    return { label: `Scaduto da ${-giorni}gg`, variant: "danger" };
  if (giorni <= 30)
    return { label: `Scade tra ${giorni}gg`, variant: "warning" };
  if (giorni <= 90) return { label: `Scade tra ${giorni}gg`, variant: "info" };
  return { label: "Valido", variant: "success" };
}

function DettaglioFornitorePage() {
  const { id } = useParams();
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [fornitore, setFornitore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contratti, setContratti] = useState([]);
  const [scadenze, setScadenze] = useState([]);
  const [rsaCollegate, setRsaCollegate] = useState([]);
  const [referenti, setReferenti] = useState([]);
  const [utentiAssociati, setUtentiAssociati] = useState([]);
  const [tipologieIntervento, setTipologieIntervento] = useState([]);
  const [valutazioni, setValutazioni] = useState([]);
  const [loadingContratti, setLoadingContratti] = useState(false);
  const [loadingScadenze, setLoadingScadenze] = useState(false);
  const [loadingRsa, setLoadingRsa] = useState(false);
  const [loadingReferenti, setLoadingReferenti] = useState(false);
  const [loadingUtenti, setLoadingUtenti] = useState(false);
  const [loadingTipologie, setLoadingTipologie] = useState(false);
  const [interventi, setInterventi] = useState([]);
  const [loadingInterventi, setLoadingInterventi] = useState(false);
  const [filtroRsaInterventi, setFiltroRsaInterventi] = useState("");
  const [activeTab, setActiveTab] = useState("info");

  // Modal states
  const [showReferenteModal, setShowReferenteModal] = useState(false);
  const [editingReferente, setEditingReferente] = useState(null);
  const [referenteForm, setReferenteForm] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    ruolo: "",
    principale: false,
  });
  const [savingReferente, setSavingReferente] = useState(false);

  // Documenti fornitore
  const [documenti, setDocumenti] = useState([]);
  const [loadingDocumenti, setLoadingDocumenti] = useState(false);
  const [showDocumentoModal, setShowDocumentoModal] = useState(false);
  const [documentoForm, setDocumentoForm] = useState({
    tag: "",
    tipoDocumento: 100000005,
    dataDocumento: "",
    dataScadenza: "",
    descrizione: "",
    file: null,
  });
  const [savingDocumento, setSavingDocumento] = useState(false);

  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const isFornitore = user?.ruolo === 100000005;
  const isOwnFornitore = isFornitore && user?.fornitore?.id === id;
  const canEdit = isAdmin || isRspp;
  const canManageDocumenti = canEdit || isOwnFornitore;

  const loadReferenti = useCallback(async () => {
    setLoadingReferenti(true);
    try {
      const data = await apiRequest(`/fornitori/${id}/referenti`);
      setReferenti(data.value || []);
    } catch (error) {
      console.error("Error loading referenti:", error);
    } finally {
      setLoadingReferenti(false);
    }
  }, [id, apiRequest]);

  const loadDocumenti = useCallback(async () => {
    setLoadingDocumenti(true);
    try {
      const data = await apiRequest(`/fornitori/${id}/documenti`);
      setDocumenti(data.value || []);
    } catch (error) {
      console.error("Error loading documenti:", error);
    } finally {
      setLoadingDocumenti(false);
    }
  }, [id, apiRequest]);

  const loadUtentiAssociati = useCallback(async () => {
    setLoadingUtenti(true);
    try {
      const data = await apiRequest(`/fornitori/${id}/utenti`);
      setUtentiAssociati(data.value || []);
    } catch (error) {
      console.error("Error loading utenti:", error);
    } finally {
      setLoadingUtenti(false);
    }
  }, [id, apiRequest]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const fornitoreData = await apiRequest(`/fornitori/${id}`);
        setFornitore(fornitoreData);

        // Carica contratti, scadenze, RSA, referenti, utenti, tipologie e documenti in parallelo
        setLoadingContratti(true);
        setLoadingScadenze(true);
        setLoadingRsa(true);
        setLoadingReferenti(true);
        setLoadingUtenti(true);
        setLoadingTipologie(true);
        setLoadingDocumenti(true);
        setLoadingInterventi(true);

        const [
          contrattiData,
          scadenzeData,
          rsaData,
          referentiData,
          utentiData,
          tipologieData,
          valutazioniData,
          documentiData,
          interventiData,
        ] = await Promise.all([
          apiRequest(`/fornitori/${id}/contratti`).catch(() => ({ value: [] })),
          apiRequest(`/fornitori/${id}/scadenze`).catch(() => ({ value: [] })),
          apiRequest(`/fornitori/${id}/rsa`).catch(() => ({ value: [] })),
          apiRequest(`/fornitori/${id}/referenti`).catch(() => ({ value: [] })),
          apiRequest(`/fornitori/${id}/utenti`).catch(() => ({ value: [] })),
          apiRequest(`/fornitori/${id}/tipologie-intervento`).catch(() => ({
            value: [],
          })),
          apiRequest(`/fornitori/${id}/valutazioni`).catch(() => []),
          apiRequest(`/fornitori/${id}/documenti`).catch(() => ({ value: [] })),
          apiRequest(`/interventi?fornitoreId=${id}`).catch(() => ({
            value: [],
          })),
        ]);

        setContratti(contrattiData.value || []);
        setScadenze(scadenzeData.value || []);
        setRsaCollegate(rsaData.value || []);
        setReferenti(referentiData.value || []);
        setUtentiAssociati(utentiData.value || []);
        setTipologieIntervento(tipologieData.value || []);
        setValutazioni(
          Array.isArray(valutazioniData)
            ? valutazioniData
            : valutazioniData.value || [],
        );
        setDocumenti(documentiData.value || []);
        setInterventi(interventiData.value || []);
      } catch (error) {
        console.error("Error fetching fornitore:", error);
        showToast("Errore nel caricamento del fornitore", "error");
        navigate("/fornitori");
      } finally {
        setLoading(false);
        setLoadingContratti(false);
        setLoadingScadenze(false);
        setLoadingRsa(false);
        setLoadingReferenti(false);
        setLoadingUtenti(false);
        setLoadingTipologie(false);
        setLoadingDocumenti(false);
        setLoadingInterventi(false);
      }
    };

    fetchData();
  }, [id, apiRequest, navigate, showToast]);

  // Referente handlers
  const handleAddReferente = () => {
    setEditingReferente(null);
    setReferenteForm({
      nome: "",
      cognome: "",
      email: "",
      telefono: "",
      ruolo: "",
      principale: false,
    });
    setShowReferenteModal(true);
  };

  const handleEditReferente = (referente) => {
    setEditingReferente(referente);
    setReferenteForm({
      nome: referente.nome || "",
      cognome: referente.cognome || "",
      email: referente.email || "",
      telefono: referente.telefono || "",
      ruolo: referente.ruolo || "",
      principale: referente.principale || false,
    });
    setShowReferenteModal(true);
  };

  const handleSaveReferente = async () => {
    if (!referenteForm.nome && !referenteForm.cognome) {
      showToast("Nome o cognome obbligatorio", "error");
      return;
    }

    setSavingReferente(true);
    try {
      if (editingReferente) {
        await apiRequest(`/admin/referenti/${editingReferente.id}`, {
          method: "PUT",
          body: JSON.stringify(referenteForm),
        });
        showToast("Referente aggiornato", "success");
      } else {
        await apiRequest(`/admin/fornitori/${id}/referenti`, {
          method: "POST",
          body: JSON.stringify(referenteForm),
        });
        showToast("Referente creato", "success");
      }
      setShowReferenteModal(false);
      loadReferenti();
    } catch (error) {
      console.error("Error saving referente:", error);
      showToast("Errore nel salvataggio del referente", "error");
    } finally {
      setSavingReferente(false);
    }
  };

  const handleDeleteReferente = async (referente) => {
    if (!confirm(`Eliminare il referente ${referente.nomeCompleto}?`)) {
      return;
    }

    try {
      await apiRequest(`/admin/referenti/${referente.id}`, {
        method: "DELETE",
      });
      showToast("Referente eliminato", "success");
      loadReferenti();
    } catch (error) {
      console.error("Error deleting referente:", error);
      showToast("Errore nell'eliminazione del referente", "error");
    }
  };

  // Documento handlers
  const handleAddDocumento = () => {
    setDocumentoForm({
      tag: "",
      tipoDocumento: 100000005,
      dataDocumento: "",
      dataScadenza: "",
      descrizione: "",
      file: null,
    });
    setShowDocumentoModal(true);
  };

  const handleSaveDocumento = async () => {
    if (!documentoForm.tag.trim()) {
      showToast("Inserisci un tag/etichetta per il documento", "error");
      return;
    }
    if (!documentoForm.file) {
      showToast("Seleziona un file da caricare", "error");
      return;
    }

    setSavingDocumento(true);
    try {
      const fd = new FormData();
      fd.append("file", documentoForm.file);
      fd.append("nome", documentoForm.tag.trim());
      fd.append("tipoDocumento", String(documentoForm.tipoDocumento));
      fd.append("fornitoreId", id);
      if (documentoForm.dataDocumento)
        fd.append("dataDocumento", documentoForm.dataDocumento);
      if (documentoForm.dataScadenza)
        fd.append("dataScadenza", documentoForm.dataScadenza);
      if (documentoForm.descrizione)
        fd.append("descrizione", documentoForm.descrizione);
      fd.append("visibileFornitore", "true");

      await apiRequest("/documenti/upload", { method: "POST", body: fd });
      showToast("Documento caricato", "success");
      setShowDocumentoModal(false);
      loadDocumenti();
    } catch (error) {
      console.error("Error uploading documento:", error);
      showToast(
        error.message || "Errore nel caricamento del documento",
        "error",
      );
    } finally {
      setSavingDocumento(false);
    }
  };

  const handleDeleteDocumento = async (doc) => {
    if (!confirm(`Eliminare il documento "${doc.nome}"?`)) return;
    try {
      await apiRequest(`/fornitori/${id}/documenti/${doc.id}`, {
        method: "DELETE",
      });
      showToast("Documento eliminato", "success");
      loadDocumenti();
    } catch (error) {
      console.error("Error deleting documento:", error);
      showToast("Errore nell'eliminazione del documento", "error");
    }
  };

  const rsaColumns = [
    {
      key: "nome",
      label: "RSA",
      render: (row) => (
        <Link
          to={`/rsa/${row.id}`}
          className="text-primary-600 hover:underline font-medium"
        >
          {row.nome}
        </Link>
      ),
    },
    {
      key: "comune",
      label: "Comune",
      render: (row) => row.comune || "-",
    },
    {
      key: "indirizzo",
      label: "Indirizzo",
      render: (row) => row.indirizzo || "-",
    },
  ];

  const contrattiColumns = [
    {
      key: "nome",
      label: "Contratto",
      render: (row) => (
        <Link
          to={`/contratti/${row.id}`}
          className="text-primary-600 hover:underline font-medium"
        >
          {row.nome}
        </Link>
      ),
    },
    {
      key: "codice",
      label: "Codice",
      render: (row) => row.codice || "-",
    },
    {
      key: "rsa",
      label: "RSA",
      render: (row) => row.rsa || "Tutte",
    },
    {
      key: "dataFine",
      label: "Scadenza",
      render: (row) =>
        row.dataFine ? new Date(row.dataFine).toLocaleDateString("it-IT") : "-",
    },
    {
      key: "stato",
      label: "Stato",
      render: (row) => {
        const stati = {
          100000000: { label: "Attivo", variant: "success" },
          100000001: { label: "In scadenza", variant: "warning" },
          100000002: { label: "Scaduto", variant: "danger" },
          100000003: { label: "Annullato", variant: "default" },
        };
        const s = stati[row.stato] || { label: "N/D", variant: "default" };
        return <Badge variant={s.variant}>{s.label}</Badge>;
      },
    },
  ];

  const scadenzeColumns = [
    {
      key: "nome",
      label: "Scadenza",
      render: (row) => (
        <Link
          to={`/scadenze/${row.id}`}
          className="text-primary-600 hover:underline font-medium"
        >
          {row.nome}
        </Link>
      ),
    },
    {
      key: "dataScadenza",
      label: "Data",
      render: (row) =>
        row.dataScadenza
          ? new Date(row.dataScadenza).toLocaleDateString("it-IT")
          : "-",
    },
    {
      key: "rsa",
      label: "RSA",
      render: (row) => row.rsa || "-",
    },
    {
      key: "tipologiaIntervento",
      label: "Tipologia",
      render: (row) => row.tipologiaIntervento || "-",
    },
    {
      key: "stato",
      label: "Stato",
      render: (row) => {
        const stati = {
          100000000: { label: "Programmata", variant: "info" },
          100000001: { label: "In corso", variant: "warning" },
          100000002: { label: "Completata", variant: "success" },
          100000003: { label: "Scaduta", variant: "danger" },
        };
        const s = stati[row.stato] || { label: "N/D", variant: "default" };
        return <Badge variant={s.variant}>{s.label}</Badge>;
      },
    },
  ];

  // RSA presenti negli interventi (anche se non piu collegate al fornitore)
  const rsaInterventi = Array.from(
    new Map(interventi.filter((i) => i.rsa).map((i) => [i.rsa.id, i.rsa.nome])),
  ).sort((a, b) => (a[1] || "").localeCompare(b[1] || ""));

  const interventiFiltrati = filtroRsaInterventi
    ? interventi.filter((i) => i.rsa?.id === filtroRsaInterventi)
    : interventi;

  const interventiColumns = [
    {
      key: "nome",
      label: "Intervento",
      render: (row) => (
        <Link
          to={`/interventi/${row.id}`}
          className="text-primary-600 hover:underline font-medium"
        >
          {row.nome || "-"}
        </Link>
      ),
    },
    {
      key: "dataIntervento",
      label: "Data",
      render: (row) =>
        row.dataIntervento
          ? new Date(row.dataIntervento).toLocaleDateString("it-IT")
          : "-",
    },
    {
      key: "rsa",
      label: "RSA",
      render: (row) => row.rsa?.nome || "-",
    },
    {
      key: "tipologiaIntervento",
      label: "Tipologia",
      render: (row) => row.tipologiaIntervento || "-",
    },
    {
      key: "statoValidazione",
      label: "Stato",
      render: (row) => {
        if (row.statoValidazione === 100000002)
          return <Badge variant="danger">Azione correttiva</Badge>;
        if (row.statoValidazione === 100000003)
          return <Badge variant="success">Validato (AC)</Badge>;
        if (row.statoValidazione === 100000001)
          return <Badge variant="success">Validato</Badge>;
        return <Badge variant="warning">Da validare</Badge>;
      },
    },
  ];

  const referentiColumns = [
    {
      key: "nomeCompleto",
      label: "Nome",
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.nomeCompleto || "-"}</span>
          {row.principale && (
            <Badge variant="success" size="sm">
              Principale
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "ruolo",
      label: "Ruolo",
      render: (row) => row.ruolo || "-",
    },
    {
      key: "email",
      label: "Email",
      render: (row) =>
        row.email ? (
          <a
            href={`mailto:${row.email}`}
            className="text-primary-600 hover:underline"
          >
            {row.email}
          </a>
        ) : (
          "-"
        ),
    },
    {
      key: "telefono",
      label: "Telefono",
      render: (row) =>
        row.telefono ? (
          <a
            href={`tel:${row.telefono}`}
            className="text-primary-600 hover:underline"
          >
            {row.telefono}
          </a>
        ) : (
          "-"
        ),
    },
    ...(canEdit
      ? [
          {
            key: "actions",
            label: "",
            render: (row) => (
              <div className="flex gap-2 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditReferente(row)}
                >
                  ✏️
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteReferente(row)}
                >
                  🗑️
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  const utentiColumns = [
    {
      key: "displayName",
      label: "Nome",
      render: (row) => (
        <Link
          to={`/admin/utenti/${row.id}`}
          className="text-primary-600 hover:underline font-medium"
        >
          {row.displayName}
        </Link>
      ),
    },
    {
      key: "email",
      label: "Email",
      render: (row) => row.email || "-",
    },
    {
      key: "ruoloLabel",
      label: "Ruolo",
      render: (row) => row.ruoloLabel || "-",
    },
    {
      key: "attivo",
      label: "Stato",
      render: (row) => (
        <Badge variant={row.attivo ? "success" : "danger"}>
          {row.attivo ? "Attivo" : "Inattivo"}
        </Badge>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  if (!fornitore) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Fornitore non trovato</p>
        <Button onClick={() => navigate("/fornitori")} className="mt-4">
          Torna alla lista
        </Button>
      </div>
    );
  }

  const statoInfo = STATO_OPTIONS[fornitore.stato] || {
    label: "N/D",
    variant: "default",
  };

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
        <Link to="/fornitori" className="hover:text-primary-600">
          Fornitori
        </Link>
        <span>/</span>
        <span className="text-gray-900">{fornitore.nome}</span>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900">
              {fornitore.nome}
            </h1>
            <Badge variant={statoInfo.variant} dot>
              {statoInfo.label}
            </Badge>
            {fornitore.abilitatoPortale && (
              <Badge variant="info">Portale</Badge>
            )}
          </div>
          <p className="text-gray-600">
            {fornitore.partitaIva && `P.IVA: ${fornitore.partitaIva}`}
            {fornitore.partitaIva && fornitore.codiceInterno && " | "}
            {fornitore.codiceInterno && `Cod: ${fornitore.codiceInterno}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/fornitori")}>
            Indietro
          </Button>
          {canEdit && (
            <Button onClick={() => navigate(`/admin/fornitori/nuovo?id=${id}`)}>
              Modifica
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-6 overflow-x-auto">
        {[
          { id: "info", label: "Informazioni", icon: "ℹ️" },
          {
            id: "tipologie",
            label: `Tipologie (${tipologieIntervento.length})`,
            icon: "🔧",
          },
          {
            id: "referenti",
            label: `Referenti (${referenti.length})`,
            icon: "👥",
          },
          { id: "rsa", label: `RSA (${rsaCollegate.length})`, icon: "🏥" },
          {
            id: "contratti",
            label: `Contratti (${contratti.length})`,
            icon: "📄",
          },
          {
            id: "scadenze",
            label: `Scadenze (${scadenze.length})`,
            icon: "📅",
          },
          {
            id: "interventi",
            label: `Interventi (${interventi.length})`,
            icon: "🛠️",
          },
          {
            id: "valutazioni",
            label: `Valutazioni (${valutazioni.length})`,
            icon: "⭐",
          },
          {
            id: "documenti",
            label: `Documenti (${documenti.length})`,
            icon: "📎",
          },
          ...(canEdit
            ? [
                {
                  id: "config-doc",
                  label: "Config Doc",
                  icon: "📁",
                },
                {
                  id: "utenti",
                  label: `Utenti (${utentiAssociati.length})`,
                  icon: "👤",
                },
              ]
            : []),
          ...(isAdmin
            ? [{ id: "api", label: "Integrazione API", icon: "🔑" }]
            : []),
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === tab.id
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Info */}
      {activeTab === "info" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Dati Anagrafici */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span>🏢</span> Dati Anagrafici
            </h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Ragione Sociale</dt>
                <dd className="font-medium">{fornitore.nome}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Partita IVA</dt>
                <dd className="font-medium">{fornitore.partitaIva || "-"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Codice Fiscale</dt>
                <dd className="font-medium">
                  {fornitore.codiceFiscale || "-"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Codice Interno</dt>
                <dd className="font-medium">
                  {fornitore.codiceInterno || "-"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Macrocategoria</dt>
                <dd className="font-medium">
                  {MACROCATEGORIE[fornitore.macrocategoria] || "-"}
                </dd>
              </div>
            </dl>
          </Card>

          {/* Contatti */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span>📞</span> Contatti
            </h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Telefono</dt>
                <dd className="font-medium">
                  {fornitore.telefono ? (
                    <a
                      href={`tel:${fornitore.telefono}`}
                      className="text-primary-600 hover:underline"
                    >
                      {fornitore.telefono}
                    </a>
                  ) : (
                    "-"
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Email</dt>
                <dd className="font-medium">
                  {fornitore.emailPrincipale ? (
                    <a
                      href={`mailto:${fornitore.emailPrincipale}`}
                      className="text-primary-600 hover:underline"
                    >
                      {fornitore.emailPrincipale}
                    </a>
                  ) : (
                    "-"
                  )}
                </dd>
              </div>
              {fornitore.emailOperativa &&
                fornitore.emailOperativa !== fornitore.emailPrincipale && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Email Operativa</dt>
                    <dd className="font-medium">
                      <a
                        href={`mailto:${fornitore.emailOperativa}`}
                        className="text-primary-600 hover:underline"
                      >
                        {fornitore.emailOperativa}
                      </a>
                    </dd>
                  </div>
                )}
              <div className="flex justify-between">
                <dt className="text-gray-500">PEC</dt>
                <dd className="font-medium">
                  {fornitore.pec ? (
                    <a
                      href={`mailto:${fornitore.pec}`}
                      className="text-primary-600 hover:underline"
                    >
                      {fornitore.pec}
                    </a>
                  ) : (
                    "-"
                  )}
                </dd>
              </div>
              {fornitore.sitoWeb && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Sito Web</dt>
                  <dd className="font-medium">
                    <a
                      href={fornitore.sitoWeb}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      {fornitore.sitoWeb}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Sede Legale */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span>📍</span> Sede Legale
            </h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Indirizzo</dt>
                <dd className="font-medium text-right">
                  {fornitore.indirizzoSedeLegale || "-"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Comune</dt>
                <dd className="font-medium">
                  {fornitore.comuneSedeLegale || "-"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Provincia</dt>
                <dd className="font-medium">
                  {fornitore.provinciaSedeLegale || "-"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">CAP</dt>
                <dd className="font-medium">
                  {fornitore.capSedeLegale || "-"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Nazione</dt>
                <dd className="font-medium">{fornitore.nazione || "Italia"}</dd>
              </div>
            </dl>
          </Card>

          {/* Referente */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span>👤</span> Referente
            </h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Nome</dt>
                <dd className="font-medium">
                  {fornitore.nomeReferente || "-"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Email</dt>
                <dd className="font-medium">
                  {fornitore.emailReferente ? (
                    <a
                      href={`mailto:${fornitore.emailReferente}`}
                      className="text-primary-600 hover:underline"
                    >
                      {fornitore.emailReferente}
                    </a>
                  ) : (
                    "-"
                  )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Telefono</dt>
                <dd className="font-medium">
                  {fornitore.telefonoReferente ? (
                    <a
                      href={`tel:${fornitore.telefonoReferente}`}
                      className="text-primary-600 hover:underline"
                    >
                      {fornitore.telefonoReferente}
                    </a>
                  ) : (
                    "-"
                  )}
                </dd>
              </div>
            </dl>
          </Card>

          {/* Dati Bancari */}
          {fornitore.iban && (
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span>🏦</span> Dati Bancari
              </h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-gray-500 text-sm">IBAN</dt>
                  <dd className="font-mono text-sm mt-1">{fornitore.iban}</dd>
                </div>
              </dl>
            </Card>
          )}

          {/* Note */}
          {fornitore.note && (
            <Card className="lg:col-span-2">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Note</h2>
              <p className="text-gray-700 whitespace-pre-wrap">
                {fornitore.note}
              </p>
            </Card>
          )}
        </div>
      )}

      {/* Tab: Tipologie Intervento */}
      {activeTab === "tipologie" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Tipologie Intervento Abilitate
            </h2>
          </div>
          {loadingTipologie ? (
            <div className="flex justify-center py-8">
              <div className="spinner w-8 h-8"></div>
            </div>
          ) : tipologieIntervento.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {tipologieIntervento.map((tip) => (
                <div key={tip.id} className="p-3 border rounded-lg bg-gray-50">
                  <div className="font-medium text-gray-900">{tip.nome}</div>
                  {tip.descrizione && (
                    <div className="text-sm text-gray-500 mt-1">
                      {tip.descrizione}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              Nessuna tipologia intervento associata a questo fornitore
            </p>
          )}
        </Card>
      )}

      {/* Tab: RSA */}
      {activeTab === "rsa" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              RSA Collegate
            </h2>
          </div>
          <DataTable
            data={rsaCollegate}
            columns={rsaColumns}
            loading={loadingRsa}
            searchable
            searchPlaceholder="Cerca RSA..."
            emptyMessage="Nessuna RSA collegata a questo fornitore"
          />
        </Card>
      )}

      {/* Tab: Contratti */}
      {activeTab === "contratti" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Contratti</h2>
            {canEdit && (
              <Button
                onClick={() => navigate(`/contratti/nuovo?fornitoreId=${id}`)}
              >
                + Nuovo Contratto
              </Button>
            )}
          </div>
          <DataTable
            data={contratti}
            columns={contrattiColumns}
            loading={loadingContratti}
            searchable
            searchPlaceholder="Cerca contratto..."
            emptyMessage="Nessun contratto per questo fornitore"
          />
        </Card>
      )}

      {/* Tab: Scadenze */}
      {activeTab === "scadenze" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Scadenze Assegnate
            </h2>
            <Link
              to={`/scadenze?fornitoreId=${id}`}
              className="text-sm text-primary-600 hover:underline"
            >
              Vedi tutte le scadenze
            </Link>
          </div>
          <DataTable
            data={scadenze}
            columns={scadenzeColumns}
            loading={loadingScadenze}
            searchable
            searchPlaceholder="Cerca scadenza..."
            emptyMessage="Nessuna scadenza assegnata a questo fornitore"
          />
        </Card>
      )}

      {/* Tab: Interventi */}
      {activeTab === "interventi" && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Interventi Registrati
            </h2>
            <div className="flex items-center gap-2">
              <label
                htmlFor="filtro-rsa-interventi"
                className="text-sm text-gray-600"
              >
                RSA
              </label>
              <select
                id="filtro-rsa-interventi"
                value={filtroRsaInterventi}
                onChange={(e) => setFiltroRsaInterventi(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
              >
                <option value="">Tutte ({interventi.length})</option>
                {rsaInterventi.map(([rsaId, rsaNome]) => (
                  <option key={rsaId} value={rsaId}>
                    {rsaNome} (
                    {interventi.filter((i) => i.rsa?.id === rsaId).length})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <DataTable
            data={interventiFiltrati}
            columns={interventiColumns}
            loading={loadingInterventi}
            searchable
            searchPlaceholder="Cerca intervento..."
            emptyMessage="Nessun intervento registrato per questo fornitore"
          />
        </Card>
      )}

      {/* Tab: Referenti */}
      {activeTab === "referenti" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Referenti Fornitore
            </h2>
            {canEdit && (
              <Button onClick={handleAddReferente}>+ Nuovo Referente</Button>
            )}
          </div>
          <DataTable
            data={referenti}
            columns={referentiColumns}
            loading={loadingReferenti}
            searchable
            searchPlaceholder="Cerca referente..."
            emptyMessage="Nessun referente per questo fornitore"
          />
        </Card>
      )}

      {/* Tab: Config Doc Valutazione */}
      {activeTab === "config-doc" && canEdit && (
        <FornitoreConfigDocTab fornitoreId={id} />
      )}

      {/* Tab: Integrazione API */}
      {activeTab === "api" && isAdmin && (
        <FornitoreApiTab fornitoreId={id} fornitoreNome={fornitore?.nome} />
      )}

      {/* Tab: Utenti */}
      {activeTab === "utenti" && canEdit && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Utenti Portale Associati
            </h2>
            <p className="text-sm text-gray-500">
              Gestisci gli utenti dal pannello{" "}
              <Link
                to="/admin/utenti"
                className="text-primary-600 hover:underline"
              >
                Utenti
              </Link>
            </p>
          </div>
          <DataTable
            data={utentiAssociati}
            columns={utentiColumns}
            loading={loadingUtenti}
            searchable
            searchPlaceholder="Cerca utente..."
            emptyMessage="Nessun utente associato a questo fornitore"
          />
        </Card>
      )}

      {/* Tab: Valutazioni */}
      {activeTab === "valutazioni" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Storico Valutazioni
            </h2>
            <Button
              size="sm"
              onClick={() => navigate("/valutazione-fornitori")}
            >
              Gestisci Valutazioni
            </Button>
          </div>
          {/* Mini-trend sparkline */}
          {valutazioni.length >= 2 && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-2">
                Trend punteggio (ultimi {Math.min(valutazioni.length, 6)}{" "}
                periodi)
              </p>
              <div className="flex items-end gap-1 h-16">
                {valutazioni
                  .slice(0, 6)
                  .reverse()
                  .map((v, i) => {
                    const score = v.phc_punteggiofinale;
                    const h =
                      score != null ? Math.max(4, (score / 100) * 64) : 4;
                    const color =
                      score == null
                        ? "bg-gray-200"
                        : score >= 80
                          ? "bg-green-500"
                          : score >= 60
                            ? "bg-blue-500"
                            : score >= 40
                              ? "bg-orange-500"
                              : "bg-red-500";
                    return (
                      <div
                        key={i}
                        className="flex flex-col items-center gap-1 flex-1"
                      >
                        <span className="text-[10px] text-gray-500 font-mono">
                          {score != null ? score : "-"}
                        </span>
                        <div
                          className={`w-full rounded-t ${color}`}
                          style={{ height: `${h}px` }}
                          title={`${v.phc_periodo}: ${score ?? "N/D"}`}
                        />
                        <span className="text-[9px] text-gray-400">
                          {v.phc_periodo?.replace(/^\d{4}-/, "")}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
          {valutazioni.length === 0 ? (
            <p className="text-gray-500 text-sm py-4">
              Nessuna valutazione disponibile per questo fornitore.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">
                      Periodo
                    </th>
                    <th className="text-center px-4 py-2 font-medium text-gray-600">
                      Punteggio
                    </th>
                    <th className="text-center px-4 py-2 font-medium text-gray-600">
                      Classificazione
                    </th>
                    <th className="text-center px-4 py-2 font-medium text-gray-600">
                      Stato
                    </th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {valutazioni.map((v) => {
                    const classColors = {
                      100000000: "bg-green-100 text-green-800",
                      100000001: "bg-blue-100 text-blue-800",
                      100000002: "bg-orange-100 text-orange-800",
                      100000003: "bg-red-100 text-red-800",
                    };
                    const classLabels = {
                      100000000: "A",
                      100000001: "B",
                      100000002: "C",
                      100000003: "D",
                    };
                    const statoLabels = {
                      100000000: "Bozza",
                      100000001: "Completata",
                      100000002: "Approvata",
                    };
                    return (
                      <tr
                        key={v.phc_valutazionefornitoreid}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="px-4 py-3 font-medium">
                          {v.phc_periodo}
                        </td>
                        <td className="px-4 py-3 text-center font-mono font-bold">
                          {v.phc_punteggiofinale != null
                            ? v.phc_punteggiofinale
                            : "-"}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {v.phc_classificazione != null ? (
                            <span
                              className={`text-xs font-semibold px-2 py-1 rounded ${classColors[v.phc_classificazione] || ""}`}
                            >
                              {classLabels[v.phc_classificazione]}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-xs">
                          {statoLabels[v.phc_stato] || "-"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() =>
                              navigate(
                                `/valutazione-fornitori/${v.phc_valutazionefornitoreid}`,
                              )
                            }
                            className="text-blue-600 hover:underline text-sm"
                          >
                            Dettaglio
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Tab: Documenti */}
      {activeTab === "documenti" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Documenti e Certificazioni
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Certificazioni (ISO 9001, ISO 14001, SOA...), DURC, polizze
                assicurative, visure camerali e altri documenti del fornitore.
              </p>
            </div>
            {canManageDocumenti && (
              <Button onClick={handleAddDocumento}>+ Nuovo Documento</Button>
            )}
          </div>
          {loadingDocumenti ? (
            <div className="flex justify-center py-8">
              <div className="spinner w-8 h-8"></div>
            </div>
          ) : documenti.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              Nessun documento caricato per questo fornitore.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">
                      Tag
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">
                      Tipologia
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">
                      Scadenza
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">
                      Stato
                    </th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {documenti.map((doc) => {
                    const stato = statoScadenza(doc.dataScadenza);
                    return (
                      <tr key={doc.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">
                            {doc.nome}
                          </div>
                          {doc.descrizione && (
                            <div className="text-xs text-gray-500 mt-1">
                              {doc.descrizione}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="info" size="sm">
                            {TIPI_DOC_FORNITORE_MAP[doc.tipoDocumento] || "-"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">
                          {doc.dataScadenza
                            ? new Date(doc.dataScadenza).toLocaleDateString(
                                "it-IT",
                              )
                            : "-"}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={stato.variant} size="sm">
                            {stato.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex gap-2 justify-end">
                            {doc.urlSharepoint && (
                              <a
                                href={doc.urlSharepoint}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:underline text-sm"
                              >
                                Apri
                              </a>
                            )}
                            {canManageDocumenti && (
                              <button
                                onClick={() => handleDeleteDocumento(doc)}
                                className="text-red-600 hover:underline text-sm"
                              >
                                Elimina
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Modal: Nuovo Documento Fornitore */}
      <Modal
        isOpen={showDocumentoModal}
        onClose={() => setShowDocumentoModal(false)}
        title="Nuovo Documento Fornitore"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipologia *
            </label>
            <select
              value={documentoForm.tipoDocumento}
              onChange={(e) =>
                setDocumentoForm({
                  ...documentoForm,
                  tipoDocumento: parseInt(e.target.value),
                  tag: "",
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {TIPI_DOC_FORNITORE.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tag / Etichetta *
            </label>
            <input
              type="text"
              list="tag-suggestions"
              value={documentoForm.tag}
              onChange={(e) =>
                setDocumentoForm({ ...documentoForm, tag: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="es. ISO 9001:2015, DURC Aprile 2026, RCT/RCO..."
            />
            <datalist id="tag-suggestions">
              {(TAG_SUGGERITI[documentoForm.tipoDocumento] || []).map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            <p className="text-xs text-gray-500 mt-1">
              Etichetta libera che identifica il documento (es. nome
              certificazione, periodo, ente emittente).
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data documento
              </label>
              <input
                type="date"
                value={documentoForm.dataDocumento}
                onChange={(e) =>
                  setDocumentoForm({
                    ...documentoForm,
                    dataDocumento: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data scadenza
              </label>
              <input
                type="date"
                value={documentoForm.dataScadenza}
                onChange={(e) =>
                  setDocumentoForm({
                    ...documentoForm,
                    dataScadenza: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note (opzionale)
            </label>
            <textarea
              rows="2"
              value={documentoForm.descrizione}
              onChange={(e) =>
                setDocumentoForm({
                  ...documentoForm,
                  descrizione: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Note aggiuntive sul documento"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              File *
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip"
              onChange={(e) =>
                setDocumentoForm({
                  ...documentoForm,
                  file: e.target.files?.[0] || null,
                })
              }
              className="w-full text-sm"
            />
            {documentoForm.file && (
              <p className="text-xs text-gray-500 mt-1">
                {documentoForm.file.name} (
                {(documentoForm.file.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowDocumentoModal(false)}
              disabled={savingDocumento}
            >
              Annulla
            </Button>
            <Button onClick={handleSaveDocumento} disabled={savingDocumento}>
              {savingDocumento ? "Caricamento..." : "Carica"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal: Nuovo/Modifica Referente */}
      <Modal
        isOpen={showReferenteModal}
        onClose={() => setShowReferenteModal(false)}
        title={editingReferente ? "Modifica Referente" : "Nuovo Referente"}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome
              </label>
              <input
                type="text"
                value={referenteForm.nome}
                onChange={(e) =>
                  setReferenteForm({ ...referenteForm, nome: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Nome"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cognome
              </label>
              <input
                type="text"
                value={referenteForm.cognome}
                onChange={(e) =>
                  setReferenteForm({
                    ...referenteForm,
                    cognome: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Cognome"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={referenteForm.email}
              onChange={(e) =>
                setReferenteForm({ ...referenteForm, email: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="email@esempio.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefono
              </label>
              <input
                type="tel"
                value={referenteForm.telefono}
                onChange={(e) =>
                  setReferenteForm({
                    ...referenteForm,
                    telefono: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="+39 ..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ruolo
              </label>
              <input
                type="text"
                value={referenteForm.ruolo}
                onChange={(e) =>
                  setReferenteForm({ ...referenteForm, ruolo: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="es. Responsabile tecnico"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="principale"
              checked={referenteForm.principale}
              onChange={(e) =>
                setReferenteForm({
                  ...referenteForm,
                  principale: e.target.checked,
                })
              }
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="principale" className="text-sm text-gray-700">
              Referente principale
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowReferenteModal(false)}
            >
              Annulla
            </Button>
            <Button onClick={handleSaveReferente} disabled={savingReferente}>
              {savingReferente ? "Salvataggio..." : "Salva"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default DettaglioFornitorePage;
